import { ChangeEvent, FormEvent, MouseEvent, useState } from "react";

import { toDateString } from "~/libs";

import {
  ApiMembersSkillEvaluationTermsIndexParamsType,
  useInput,
  useApisMembersSkillEvaluationTermsIndex,
  useDateRange,
  useArray,
  useProvidersEmployeeLayout,
} from "~/hooks";

import { RangeDatePropsType } from "~/components/molecules";

import { SkillEvaluationTermType, EmployeeRole } from "~/domains";

type ReturnType = {
  managersSkillEvaluationTerms: SkillEvaluationTermType[];
  isManagersDataLoading: boolean;
  ownersSkillEvaluationTerms: SkillEvaluationTermType[];
  isOwnersDataLoading: boolean;
  titleKeyword: string;
  startDate: Date | null;
  endDate: Date | null;
  onChangeDateRange: (value: RangeDatePropsType) => void;
  onChangeTitleKeyword: (e: ChangeEvent<HTMLInputElement>) => void;
  managersHasNextPage?: boolean;
  managersFetchNextPage: () => void;
  ownersHasNextPage?: boolean;
  ownersFetchNextPage: () => void;
  onSearchSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onConditionReset: (e: MouseEvent<HTMLButtonElement>) => void;
};

type PropsType = {
  inTerm: boolean;
};

export const useSearchMembersSkillEvaluationTerms = ({
  inTerm,
}: PropsType): ReturnType => {
  const { setFalseRightSidebarMain } = useProvidersEmployeeLayout();
  const [
    { value: titleKeyword, onChange: onChangeTitleKeyword },
    setTitleKeyword,
  ] = useInput("");
  const [{ startDate, endDate, onChange: onChangeDateRange }] = useDateRange();
  const [params, setParams] =
    useState<ApiMembersSkillEvaluationTermsIndexParamsType>();

  const {
    items: managersSkillEvaluationTerms,
    setItems: setManagersSkillEvaluationTerms,
  } = useArray<SkillEvaluationTermType>([]);

  const {
    items: ownersSkillEvaluationTerms,
    setItems: setOwnersSkillEvaluationTerms,
  } = useArray<SkillEvaluationTermType>([]);

  const {
    fetchNextPage: managersFetchNextPage,
    hasNextPage: managersHasNextPage,
    isFetching: isManagersDataLoading,
  } = useApisMembersSkillEvaluationTermsIndex({
    params: { inTerm, employeeRoleId: EmployeeRole.MANAGER.id, ...params },
    config: {
      onSuccess: (data) => {
        setManagersSkillEvaluationTerms(
          data.pages.map((page) => page.skillEvaluationTerms).flat(),
        );
      },
    },
  });

  const {
    fetchNextPage: ownersFetchNextPage,
    hasNextPage: ownersHasNextPage,
    isFetching: isOwnersDataLoading,
  } = useApisMembersSkillEvaluationTermsIndex({
    params: { inTerm, employeeRoleId: EmployeeRole.OWNER.id, ...params },
    config: {
      onSuccess: (data) => {
        setOwnersSkillEvaluationTerms(
          data.pages.map((page) => page.skillEvaluationTerms).flat(),
        );
      },
    },
  });

  const onSearchSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setParams({
      titleKeyword,
      fromDate: startDate ? toDateString(startDate) : "",
      toDate: endDate ? toDateString(endDate) : "",
    });
    setFalseRightSidebarMain();
  };

  const onConditionReset = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setTitleKeyword("");
    onChangeDateRange([null, null]);
    setParams(undefined);
    setFalseRightSidebarMain();
  };

  return {
    managersSkillEvaluationTerms,
    titleKeyword,
    managersHasNextPage,
    managersFetchNextPage,
    ownersSkillEvaluationTerms,
    ownersHasNextPage,
    startDate,
    endDate,
    isManagersDataLoading,
    isOwnersDataLoading,
    onChangeDateRange,
    ownersFetchNextPage,
    onChangeTitleKeyword,
    onSearchSubmit,
    onConditionReset,
  };
};
